import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteApiApps, UserKeys } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { ApigeeApp } from '../../../../types';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteApiAppDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function DeleteApiAppDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
  changeLogRoles,
}: DeleteApiAppDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteApiApps(selectedRows, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Apps deleted successfully!',
      });

      queryClient.setQueryData<ApigeeApp[]>(
        [UserKeys.ApigeeApps],
        (apiApps) =>
          apiApps?.filter((app) => !selectedRows.includes(app.appKey))
      );

      setChangeLogMessage('');
      setSelectedRows([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting these apps. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.api.delete_api_app, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.api,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.DeleteApiKeys.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Delete ${selectedRows.length === 1 ? 'App' : 'Apps'}`}
      dialogContent={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            flexDirection: 'column',
          }}
        >
          {selectedRows.length > 1 && (
            <Typography>
              Delete <strong>{selectedRows.length} apps</strong>?
            </Typography>
          )}
          {selectedRows.length === 1 && (
            <Typography>
              Delete <strong> {selectedRows[0]} </strong> app?
            </Typography>
          )}
          <Typography marginTop={1}>
            {
              ' Any api calls using a deleted key will return a 401 Unauthorized error.'
            }
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: `Delete ${selectedRows.length === 1 ? 'App' : 'Apps'}`,
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
